import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Button, Input } from "../../atoms";
import { FormField } from "../FormField";
import { SettingsSectionCard } from "@/components/layout/SettingsSectionCard";
import { toDateInputValue } from "@/utils/date";
import type { StartupSettingsInitialData } from "../StartupSettingsForm/StartupSettingsForm";

interface TimelineValues {
  startDate: string;
  endDate: string;
}

export interface TimelineSettingsProps {
  initialData?: StartupSettingsInitialData;
  onSubmit: (data: { startDate?: string; endDate?: string }) => Promise<void>;
  isLoading?: boolean;
}

export const TimelineSettings: React.FC<TimelineSettingsProps> = ({
  initialData,
  onSubmit,
  isLoading = false,
}) => {
  const defaultValues = useMemo((): TimelineValues => {
    const timelineStart = initialData?.startDate ?? initialData?.foundedDate;
    return {
      startDate: toDateInputValue(timelineStart),
      endDate: toDateInputValue(initialData?.endDate),
    };
  }, [initialData]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<TimelineValues>({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <SettingsSectionCard
      title="Timeline"
      description="Control the default timeline range and project end marker on your charts."
    >
      <form
        onSubmit={handleSubmit(async (data) => {
          await onSubmit({
            startDate: data.startDate || undefined,
            endDate: data.endDate || undefined,
          });
          reset(data);
        })}
        className="space-y-4"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Timeline start date">
            <Input type="date" {...register("startDate")} />
          </FormField>

          <FormField label="Project end date">
            <Input type="date" {...register("endDate")} />
          </FormField>
        </div>

        <p className="text-xs text-white/45">
          Leave project end date empty to use the default chart horizon without a
          project end marker.
        </p>

        <div className="flex justify-end border-t border-white/10 pt-4">
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={isLoading || !isDirty}
          >
            {isLoading ? "Saving..." : "Save timeline"}
          </Button>
        </div>
      </form>
    </SettingsSectionCard>
  );
};
